// frontend/src/pages/Welcome.jsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { OnboardingContext } from '../contexts/OnboardingContext';
import OnboardGuard from '../components/OnboardGuard';
import Logo from '../components/common/Logo';
import Footer from '../components/common/Footer';
import OnboardingProgressBar from '../components/common/OnboardingProgressBar';
import { Building2, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';

function WelcomeInner() {
  const navigate = useNavigate();
  const { setOnboardingStepIfNotComplete } = useContext(OnboardingContext);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  // Get firstName from sessionStorage (set during signup) or fallback
  let firstName = '';
  try {
    firstName = sessionStorage.getItem('firstName') || '';
  } catch (err) {
    console.warn('[Welcome] sessionStorage blocked (private browsing?)', err);
  }

  const handleContinue = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/api/user/onboarding', { step: 'choose_plan' });
      setOnboardingStepIfNotComplete('choose_plan');
      navigate('/choose-plan');
    } catch (err) {
      console.error('Failed to advance from welcome:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const steps = [
    {
      icon: Building2,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      title: 'Connect Your Bank',
      description: 'Securely link your accounts via Plaid to auto-detect your recurring bills and income.',
    },
    {
      icon: Sparkles,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: 'Review Your Bills',
      description: "We'll scan your transaction history and find subscriptions, bills, and paychecks automatically.",
    },
    {
      icon: TrendingUp,
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      title: 'See Your 8-Week Forecast',
      description: 'Get a clear picture of your cash flow with overdraft warnings before they happen.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Header */}
      <div className="w-full py-6 px-4 border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex justify-center">
          <Logo size="sm" />
        </div>
      </div>

      {/* Progress */}
      <OnboardingProgressBar currentStep="welcome" />

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-2xl">
          <div className="mb-8 sm:mb-10 text-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              {firstName ? `Welcome, ${firstName}!` : 'Welcome to SecureTheBag!'}
            </h1>
            <p className="text-gray-600 text-base sm:text-lg">
              Here's what we'll set up together — it only takes a few minutes.
            </p>
          </div>

          {/* Step cards */}
          <div className="space-y-4 mb-8">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-4 p-4 sm:p-5 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${step.iconBg}`}>
                  <step.icon className={`w-5 h-5 ${step.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase">Step {i + 1}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {/* CTA button */}
          <button
            onClick={handleContinue}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 min-h-[44px] px-6 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Loading...' : "Let's Get Started"}
            {!submitting && <ArrowRight className="w-5 h-5" />}
          </button>

          <p className="text-center text-sm text-gray-500 mt-6">
            Pick a plan next — your 7-day free trial starts when you add a card.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default function Welcome() {
  return (
    <OnboardGuard currentStep="welcome">
      <WelcomeInner />
    </OnboardGuard>
  );
}
